/**
 * §SLA: one deadline vocabulary for every surface that shows a workflow-manager step's SLA — the board
 * cards and column stats, the step rows in the lead's Nawy Now tab, and the agent's task queue.
 *
 * Shared rather than per-component because the underlying SLA is authored in workflow-manager as
 * {slaValue, slaUnit} where slaUnit is MINUTES | HOURS | BUSINESS_DAYS | CALENDAR_DAYS. A day-granular
 * label cannot express the first two at all: a step with a 4-hour SLA read "Due 17 Aug" all day, which
 * says nothing about whether the agent has three hours left or breached at breakfast. `plannedEndDate` is
 * a full timestamp, so the remaining window is always derivable — the formatting was the only thing
 * throwing it away.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 3600_000;
const DAY_MS = 24 * HOUR_MS;

export type SlaState = "breach" | "soon" | "ok" | "none";

export interface SlaReading {
  state: SlaState;
  /**
   * Empty when there is nothing to say — an unopened step. Callers must render NOTHING on an empty label,
   * not an empty pill: a coloured chip with no text is a smudge, and "No SLA" would be a claim (this step
   * has no deadline) rather than the truth (its deadline does not exist yet).
   */
  label: string;
}

/**
 * §BoardSla: the deadline as the pill the design shows — "Overdue 17h", "Due in 6h", "3d left".
 * Under a day either way is read in hours, and under an hour in minutes, because those are the windows an
 * agent can still act in; a MINUTES-unit SLA otherwise rounded up to a flat "1h" and lost its urgency.
 * A stage that authors no SLA at all is "No SLA", never a fabricated one. (An unopened step is a different
 * case and reads as nothing — see §SLA-unstarted below.)
 *
 * §SLA-unstarted: `started` is REQUIRED, and it is the whole difference between a deadline and a guess.
 * Workflow-manager anchors a step's `plannedEndDate` to `startedAt` only when the step ACTUALLY OPENS
 * (§SLA-anchor). Before that the field holds the forecast chain fixed at CASE CREATION — step N planned to
 * start where step N−1 planned to end — which slides into the past as the case idles upstream and is never
 * re-derived. Reading that as lateness is how an unopened step came to show "Overdue 5d": it had missed a
 * date nobody ever committed to, and no amount of working it faster could have helped.
 *
 * The rule itself is shared with workflow-manager — `OversightHelper.isOverdue`, `overdueWhere`, the
 * board-summary CTE and the WM frontend's `stepDueState` all gate on `startedAt`, and
 * `CaseTimelineHelper.slaOf` always has. Those surfaces are why WM looked correct while the CRM did not: the
 * CRM is the one that derives its own verdict from the raw date, so it was the one still accusing steps.
 *
 * Where this DELIBERATELY differs from WM: an unopened step shows nothing at all here, whereas WM's step
 * screen still renders the forecast as `planned <date>`. Both were tried. A forecast rendered on this row
 * reads as a stale deadline — "Planned 16 Aug" on the 19th looks like a date that was missed, without the
 * honesty of saying so — and it is the only absolute date in a row whose entire vocabulary is a REMAINING
 * window ("Due in 6h", "Overdue 5h"), so it lands as a different kind of fact from everything beside it.
 * The divergence is a choice about audience, not drift: WM's step screen is an ops surface where the flow's
 * plan is the point, and this is a sales surface where the only question is what is owed now.
 *
 * `started` is required rather than defaulted on purpose: the bug was a missing input, and a default would
 * let the next call site reintroduce it in silence.
 */
export const resolveSla = (
  dueAt: string | null | undefined,
  { started }: { started: boolean },
): SlaReading => {
  // Nothing is owed until the step opens, so there is nothing to render — see SlaReading.label.
  if (!started) return { state: "none", label: "" };
  if (!dueAt) return { state: "none", label: "No SLA" };
  const due = new Date(dueAt).getTime();
  if (Number.isNaN(due)) return { state: "none", label: "No SLA" };

  const remaining = due - Date.now();

  if (remaining < 0) {
    const overdue = Math.abs(remaining);
    if (overdue < HOUR_MS) {
      return { state: "breach", label: `Overdue ${Math.max(1, Math.round(overdue / MINUTE_MS))}m` };
    }
    const label =
      overdue < DAY_MS
        ? `Overdue ${Math.round(overdue / HOUR_MS)}h`
        : `Overdue ${Math.round(overdue / DAY_MS)}d`;
    return { state: "breach", label };
  }
  if (remaining < HOUR_MS) {
    return { state: "soon", label: `Due in ${Math.max(1, Math.round(remaining / MINUTE_MS))}m` };
  }
  if (remaining < DAY_MS) {
    return { state: "soon", label: `Due in ${Math.round(remaining / HOUR_MS)}h` };
  }
  return { state: "ok", label: `${Math.round(remaining / DAY_MS)}d left` };
};

/**
 * §SLA-unstarted: has this step's clock actually started, i.e. is its `plannedEndDate` a commitment rather
 * than the creation forecast? Keyed on the WM lifecycle status because that is what every board payload
 * carries — the persona step rows and the kanban's `currentStep` both serve `status` but not `startedAt`.
 * Equivalent to WM's own `startedAt`-based test: activation writes `status: IN_PROGRESS` and `startedAt` in
 * one update, and every other writer pairs `NOT_STARTED` with `startedAt: null`.
 *
 * NOT_STARTED is the only unstarted state: IN_PROGRESS and REWORK_REQUIRED have both been activated (a
 * rework reopen re-anchors the deadline and gets the full SLA again), and the settled states never reach
 * a caller that asks. An absent status is treated as unstarted — the conservative direction, since the
 * cost of that is a missing pill rather than a false accusation.
 */
export const slaClockStarted = (status?: string | null): boolean =>
  status != null && status !== "NOT_STARTED";

/**
 * The absolute deadline, for the `title` tooltip beside a relative label. Relative answers "am I about to
 * breach", which is the question on screen; the exact moment is still one hover away rather than gone.
 */
export const slaTooltip = (dueAt?: string | null): string | undefined => {
  if (!dueAt) return undefined;
  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return undefined;
  return `Due ${due.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};
